import { Component, DestroyRef, ElementRef, Input, OnInit, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { MatIconModule } from '@angular/material/icon'
import { Router } from '@angular/router'
import { PersonaTypes } from '@app/modules/personas/enums/persona-types.enum'
import { DCTSettings } from '@app/modules/personas/interfaces/dct-settings.interface'
import { Persona } from '@app/modules/personas/interfaces/persona.interface'
import { PersonaService } from '@app/modules/personas/services/persona.service'
import { LoadingDialogComponent } from '@app/modules/shared/components/loading-dialog/loading-dialog.component'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { environment } from '@environments/environment'
import { NgxTippyModule } from 'ngx-tippy-wrapper'
import { finalize } from 'rxjs/operators'
import { ServerRepresentedEvent } from '../../models/event.model'
import { EventLegalService } from '../../services/event-legal.service'
import { EventService } from '../../services/event.service'
import { DCTSetup } from '../../types/dct-setup.type'
import { DCTProductComponent } from './dct-product/dct-product.component'

@Component({
  selector: 'ngx-dct-setup',
  templateUrl: './dct-setup.component.html',
  styleUrls: ['./dct-setup.component.scss'],
  imports: [MatIconModule, ReactiveFormsModule, NgxTippyModule, DCTProductComponent, LoadingDialogComponent],
  providers: [EventLegalService]
})
export class DCTSetupComponent implements OnInit {
  @Input() public eventId?: number
  public form!: FormGroup
  public isEventFinished = signal<boolean>(false)
  public isEventIdSubmitted = signal<boolean>(false)
  public isLoading = signal<boolean>(true)
  public personas = signal<Persona[]>([])
  public dctSettings = signal<DCTSetup | null>(null)
  private _event = signal<ServerRepresentedEvent | null>(null)
  constructor(
    private _fb: FormBuilder,
    private _router: Router,
    private _eventLegalService: EventLegalService,
    private _eventService: EventService,
    private _alert: AlertService,
    private _personaService: PersonaService,
    private _ref: ElementRef,
    private _destroyRef: DestroyRef
  ) {}

  ngOnInit() {
    this._initializeForm()
    this._fetchEvent()
  }

  /**
   * Initialize the group form
   */
  private _initializeForm() {
    this.form = this._fb.group({
      eventId: this._fb.control('', Validators.required)
    })
  }

  /**
   * Fetch the event and flag if the event is finished
   */
  private _fetchEvent() {
    if (!this.eventId) {
      this.isLoading.set(false)
      this._alert.alert(Alert.ERROR, 'Could not open DCT setup because the event id is missing.')
      return
    }

    this._eventService
      .getEvent(this.eventId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (event) => {
          this._event.set(event)
          const isEventCompleted = Date.now() - Date.parse(event.public_end_date) > 0
          this.isEventFinished.set(isEventCompleted)
          this._fetchPersonas()
        },
        error: () => {
          this._alert.alert(Alert.ERROR, 'Error fetching event, returning to previous page.')
          setTimeout(() => this.navigateBack(), 3000)
        }
      })
  }

  /**
   * Fetch the personas and find a persona with DCT setup
   */
  private _fetchPersonas() {
    this._eventService
      .getEventForms(this._event().id)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((personas) => {
        // Exclude generic persona and parent personas
        this.personas.set(personas.filter((persona) => ![PersonaTypes.GENERIC, PersonaTypes.GROUP].includes(persona.type)))
        // If no personas found, show warning and return
        if (!this.personas().length) return this._alert.alert(Alert.WARNING, 'No personas found to set up DCT connection!')
        const personaWithDct = this.personas().find((persona) => !!persona.options?.dct?.eventId)
        // If no persona with DCT eventId found, stop prefetching
        if (!personaWithDct) {
          this.isLoading.set(false)
          setTimeout(() => this._setCSSVariables())
          return
        }
        this.form.get('eventId').setValue(personaWithDct.options.dct.eventId)
        this.fetchDCTMapping(true)
      })
  }

  /**
   * Fetch the DCT mapping
   * @param {boolean} isPrefetch - Is true when at least one persona has DCT setup
   */
  public fetchDCTMapping(isPrefetch: boolean = false) {
    this.isLoading.set(true)
    if (!isPrefetch && (this.form.invalid || (this.form.pristine && this.isEventIdSubmitted()))) return
    this._eventLegalService
      .getSetupDetails('DCT', this.form.get('eventId').value)
      .pipe(
        finalize(() => {
          this.isLoading.set(false)
          setTimeout(() => this._setCSSVariables())
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (settings) => {
          this.form.markAsPristine()
          this.isEventIdSubmitted.set(true)
          this.dctSettings.set(settings)
        },
        error: () => this._alert.alert(Alert.ERROR, 'Error fetching DCT setup, please contact support if the problem persists.')
      })
  }

  /**
   * Navigate back to the previous page or to the events list
   */
  public navigateBack() {
    const previousNavigation = this._router.lastSuccessfulNavigation.previousNavigation
    return previousNavigation ? this._router.navigateByUrl(previousNavigation.finalUrl) : this._router.navigate(['events', 'list'])
  }

  /**
   * Open the DCT report
   */
  public openReport() {
    window.open(`${environment.registrationsApi}dct/report/${this._event().slug}`, '_blank')
  }

  /**
   * Connect the product to the persona
   * @param {Persona} persona - The persona to connect to the product
   * @param {DCTSettings} settings - The settings to connect to the persona
   */
  public connectPersona(persona: Persona, settings: DCTSettings) {
    const dctTestPayload = { ...settings, eventId: this.form.get('eventId').value }
    // TODO: Uncomment this when the DCT product validation is ready to be used
    // this._eventLegalService
    //   .validateDCTProduct(dctTestPayload)
    //   .pipe(takeUntilDestroyed(this._destroyRef))
    //   .subscribe({
    //     next: () => this._updatePersonaWithDCT(persona, dctTestPayload),
    //     error: () => this._alert.alert(Alert.ERROR, 'Error connecting product, please contact support if the problem persists.'),
    //   });
    this._updatePersonaWithDCT(persona, dctTestPayload)
  }

  /**
   * Update the persona with the DCT settings
   * @param {Persona} personaToUpdate - The persona to update
   * @param {DCTSettings} settings - The settings to update the persona with
   */
  private _updatePersonaWithDCT(personaToUpdate: Persona, settings: DCTSettings) {
    this._personaService
      .updateForm({ id: personaToUpdate.id, options: { ...personaToUpdate.options, dct: { ...settings } } })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => (personaToUpdate.options = { ...personaToUpdate.options, dct: { ...settings } }),
        error: () => this._alert.alert(Alert.ERROR, `Error ${!!settings ? 'connecting' : 'disconnecting'} product, please contact support if the problem persists.`)
      })
  }

  /**
   * Disconnect the product from the persona
   * @param {Persona} persona - The persona to disconnect from the product
   */
  public disconnectPersona(persona: Persona) {
    this._updatePersonaWithDCT(persona, null)
  }

  /**
   * Set the CSS variables to be used in the component
   */
  private _setCSSVariables() {
    const navbarHeight = document.querySelector('app-navbar')?.getBoundingClientRect().height
    this._ref.nativeElement.style.setProperty('--navbar-height', `${navbarHeight}px`)
    const dctHeaderHeight = this._ref.nativeElement.querySelector('section.header')?.getBoundingClientRect().height
    this._ref.nativeElement.style.setProperty('--dct-header-height', `${dctHeaderHeight}px`)
  }
}
